import React, { useEffect, useMemo, useState } from 'react';
import {
  meetingService,
  Meeting,
  MeetingMemberStatus,
  MeetingPhoneListEntry,
} from '../../services/meetingService';
import { useLocale } from '../../context/LocaleContext';
import { getWorkshop2026DisplayName } from '../../services/workshop2026Service';
import { formatNameFirstLastInitial } from '../../utils/userState';
import styles from '../../styles/workshop2026Page.module.css';

interface WorkshopPhoneListSectionProps {
  meetingCode: string;
  meeting: Meeting | null;
  memberStatus: MeetingMemberStatus;
  /** Facilitator-only preview before the workshop is running / list is enabled for members. */
  previewMode?: boolean;
  onExpandPreferences?: () => void;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const WorkshopPhoneListSection: React.FC<WorkshopPhoneListSectionProps> = ({
  meetingCode,
  meeting,
  memberStatus,
  previewMode = false,
  onExpandPreferences,
}) => {
  const { t } = useLocale();
  const [phoneList, setPhoneList] = useState<MeetingPhoneListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const phoneListMode = meeting?.phone_list_visibility_mode || 'off';
  const viewerExempt = memberStatus.is_manager || memberStatus.is_admin;
  // Facilitators can always view (including Off = preview). Members need Confirmed mode.
  const canViewPhoneList =
    viewerExempt ||
    (phoneListMode === 'verified_only' &&
      (memberStatus.is_verified || memberStatus.registration_state === 'confirmed'));

  // Give-to-get: see others' numbers only if you share yours; see others' emails
  // only if you share yours. Facilitators/admins are exempt.
  const viewerSharesPhone = viewerExempt || memberStatus.include_on_printed_phone_list;
  const viewerCanSeeEmail = viewerExempt || memberStatus.include_email_on_list;

  const dash = t('workshop.2026.phone_list.dash');
  const memberFallback = t('workshop.2026.phone_list.member_fallback');
  const emailLockedPlaceholder = t('workshop.2026.phone_list.share_email_to_see');

  const sponsorLabel = (willingness: string): string => {
    if (willingness === 'yes') return t('workshop.2026.phone_list.sponsor_yes');
    if (willingness === 'temporary') return t('workshop.2026.phone_list.sponsor_temp');
    return dash;
  };

  useEffect(() => {
    if (!canViewPhoneList || !viewerSharesPhone) {
      setLoading(false);
      setPhoneList([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await meetingService.getPhoneList(meetingCode);
        if (cancelled) return;
        const list = Array.isArray(res.phone_list) ? res.phone_list : [];
        list.sort((a, b) =>
          (a.display_name || '').localeCompare(b.display_name || '', undefined, { sensitivity: 'base' })
        );
        setPhoneList(list);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('workshop.2026.phone_list.load_error'));
          setPhoneList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingCode, canViewPhoneList, viewerSharesPhone, t]);

  const displayList = useMemo(
    () =>
      phoneList.map((entry) => ({
        ...entry,
        display_name: formatNameFirstLastInitial(
          entry.display_name,
          entry.display_name || memberFallback
        ),
      })),
    [phoneList, memberFallback]
  );

  const emailCellText = (entry: MeetingPhoneListEntry): string => {
    if (entry.email) return entry.email;
    if (entry.has_email && !viewerCanSeeEmail) return emailLockedPlaceholder;
    return dash;
  };

  const handlePrint = () => {
    if (displayList.length === 0) return;
    const printWindow = window.open('', '_blank', 'width=480,height=720');
    if (!printWindow) return;
    const workshopName = getWorkshop2026DisplayName(meeting);
    const asOf = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const rows = displayList
      .map(
        (entry) => `
      <tr>
        <td>${escapeHtml(entry.display_name)}</td>
        <td class="col-phone">${escapeHtml(entry.phone || dash)}</td>
        <td class="col-email">${escapeHtml(entry.email || dash)}</td>
        <td>${escapeHtml(sponsorLabel(entry.sponsor_willingness))}</td>
      </tr>`
      )
      .join('');
    const emailNote = viewerCanSeeEmail
      ? ''
      : `<p class="privacy">${escapeHtml(t('workshop.2026.phone_list.print_email_hidden'))}</p>`;

    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${escapeHtml(t('workshop.2026.phone_list.print_title', { workshopName }))}</title>
<style>
  @page { margin: 0.85in 0.35in 0.35in 0.35in; size: auto; }
  body { font-family: "Courier New", Courier, monospace; font-size: 9pt; line-height: 1.2; color: #000; margin: 0; padding: 0.15in 0 0; }
  h1 { font-size: 10pt; font-weight: bold; margin: 0.35in 0 2px; }
  .meta { font-size: 8pt; margin: 0 0 4px; }
  .privacy { font-size: 7.5pt; margin: 0 0 6px; line-height: 1.25; }
  table { width: auto; max-width: 100%; border-collapse: collapse; table-layout: fixed; }
  col.col-name { width: 22%; }
  col.col-phone { width: 15ch; }
  col.col-email { width: 38%; }
  col.col-sponsor { width: 4.5em; }
  th, td { text-align: left; padding: 1px 6px 1px 0; vertical-align: top; border-bottom: 1px solid #ccc; }
  th { font-size: 8pt; font-weight: bold; border-bottom: 1px solid #000; }
  td.col-phone { white-space: nowrap; }
  td.col-email { word-wrap: break-word; overflow-wrap: anywhere; }
</style></head><body>
<h1>${escapeHtml(workshopName)}</h1>
<p class="meta">${escapeHtml(t('workshop.2026.phone_list.print_meta', { date: asOf }))}</p>
<p class="privacy">${escapeHtml(t('workshop.2026.phone_list.print_privacy'))}</p>
${emailNote}
<table>
  <colgroup>
    <col class="col-name" />
    <col class="col-phone" />
    <col class="col-email" />
    <col class="col-sponsor" />
  </colgroup>
  <thead><tr><th>${escapeHtml(t('workshop.2026.phone_list.col_name'))}</th><th>${escapeHtml(t('workshop.2026.phone_list.col_phone'))}</th><th>${escapeHtml(t('workshop.2026.phone_list.col_email'))}</th><th>${escapeHtml(t('workshop.2026.phone_list.col_sponsor_print'))}</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const openPreferences = () => {
    onExpandPreferences?.();
    document.getElementById('workshop-preferences')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const showLockedForPhone = canViewPhoneList && !viewerSharesPhone;

  return (
    <div className={styles.phoneListInner}>
      {previewMode && (
        <p className={styles.interestBanner} role="status">
          Facilitator preview only — participants will not see this list until the workshop starts
          and you set phone list visibility to &ldquo;Confirmed participants.&rdquo;
          {phoneListMode === 'off'
            ? ' Visibility is currently Off (recommended until Saturday).'
            : phoneListMode === 'managers_only'
              ? ' Visibility is currently set to Managers & admins.'
              : ' Visibility is currently set to Confirmed participants.'}
        </p>
      )}

      <p className={styles.helperText}>{t('workshop.2026.phone_list.give_to_get')}</p>

      {phoneListMode === 'off' && !viewerExempt && (
        <p className={styles.helperText}>{t('workshop.2026.phone_list.not_available')}</p>
      )}

      {phoneListMode !== 'off' && !canViewPhoneList && (
        <p className={styles.helperText}>{t('workshop.2026.phone_list.need_verified')}</p>
      )}

      {showLockedForPhone && (
        <div className={styles.giveToGetLocked}>
          <p className={styles.giveToGetLockedTitle}>{t('workshop.2026.phone_list.locked_title')}</p>
          <p className={styles.communityText}>{t('workshop.2026.phone_list.locked_body')}</p>
          <button type="button" className={styles.textButton} onClick={openPreferences}>
            {t('workshop.2026.phone_list.update_prefs')}
          </button>
        </div>
      )}

      {canViewPhoneList && viewerSharesPhone && loading && (
        <p className={styles.helperText}>{t('workshop.2026.phone_list.loading')}</p>
      )}
      {canViewPhoneList && viewerSharesPhone && !loading && error && (
        <p className={styles.helperText}>{error}</p>
      )}
      {canViewPhoneList && viewerSharesPhone && !loading && !error && displayList.length === 0 && (
        <>
          <p className={styles.helperText}>{t('workshop.2026.phone_list.empty')}</p>
          <button type="button" className={styles.textButton} onClick={openPreferences}>
            {t('workshop.2026.phone_list.update_prefs')}
          </button>
        </>
      )}

      {canViewPhoneList && viewerSharesPhone && !loading && !error && displayList.length > 0 && (
        <>
          {!viewerCanSeeEmail && (
            <p className={styles.helperText}>{t('workshop.2026.phone_list.share_email_hint')}</p>
          )}
          <div className={styles.phoneListTableWrap}>
            <table className={styles.phoneListTable}>
              <thead>
                <tr>
                  <th>{t('workshop.2026.phone_list.col_name')}</th>
                  <th>{t('workshop.2026.phone_list.col_phone')}</th>
                  <th>{t('workshop.2026.phone_list.col_email')}</th>
                  <th>{t('workshop.2026.phone_list.col_sponsor')}</th>
                </tr>
              </thead>
              <tbody>
                {displayList.map((entry) => (
                  <tr key={entry.user_id}>
                    <td>{entry.display_name}</td>
                    <td>{entry.phone || dash}</td>
                    <td>{emailCellText(entry)}</td>
                    <td>{sponsorLabel(entry.sponsor_willingness)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.phoneListActions}>
            <button type="button" className={styles.secondaryBtn} onClick={handlePrint}>
              {t('workshop.2026.phone_list.print')}
            </button>
            <button type="button" className={styles.textButton} onClick={openPreferences}>
              {t('workshop.2026.phone_list.update_prefs')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkshopPhoneListSection;
